import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Smartphone, Laptop, Tablet, Monitor, HardDrive, ShieldCheck, Gamepad2,
  MapPin, Clock, Phone, ShieldCheck as Shield, Home, ChevronDown, HelpCircle, ArrowRight, MessageCircle,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import { services, businessInfo, heroImage } from "../mock";
import { areas } from "../data/areas";
import { useSeo, useJsonLd, faqSchema, breadcrumbSchema, SITE_URL } from "../lib/seo";

const iconMap = { Smartphone, Laptop, Tablet, Monitor, HardDrive, ShieldCheck, Gamepad2 };
const strip = (p) => p.replace(/[^0-9]/g, "");

const localBusinessSchema = (area) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}${area.path}#business`,
  name: `Repairzone Π.Κ – Επισκευή Κινητών ${area.name}`,
  url: `${SITE_URL}${area.path}`,
  image: heroImage,
  telephone: ["+306982917870", "+306948898658"],
  priceRange: "€€",
  address: { "@type": "PostalAddress", addressLocality: "Λαμία", addressRegion: "Φθιώτιδα", addressCountry: "GR" },
  areaServed: [{ "@type": "City", name: area.name }, ...area.nearby.map((n) => ({ "@type": "Place", name: n }))],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: `Υπηρεσίες επισκευής ${area.inArea}`,
    itemListElement: services.map((s) => ({
      "@type": "Offer",
      priceCurrency: "EUR",
      price: s.price.replace(/[^0-9]/g, ""),
      priceSpecification: { "@type": "PriceSpecification", minPrice: s.price.replace(/[^0-9]/g, ""), priceCurrency: "EUR" },
      itemOffered: { "@type": "Service", name: `${s.title} ${area.name}`, description: s.desc, areaServed: area.name },
    })),
  },
});

const AreaFaq = ({ faqs }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={f.q} className={`pcb-card overflow-hidden ${open === i ? "border-emerald-500/60" : ""}`}>
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            data-testid={`area-faq-${i}`}
            className="w-full flex items-center justify-between gap-4 p-5 text-left"
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5 text-emerald-400 shrink-0" />
              <span className="text-white font-semibold">{f.q}</span>
            </div>
            <ChevronDown className={`w-5 h-5 text-emerald-300 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <div className="px-5 pb-5 pl-14 text-sm text-emerald-50/80 leading-relaxed">{f.a}</div>}
        </div>
      ))}
    </div>
  );
};

const AreaLandingPage = ({ area }) => {
  useSeo({ title: area.title, description: area.description, path: area.path, image: heroImage });
  useJsonLd(`lb-${area.slug}`, localBusinessSchema(area));
  useJsonLd(`faq-${area.slug}`, faqSchema(area.faqs));
  useJsonLd(`bc-${area.slug}`, breadcrumbSchema([{ name: "Αρχική", path: "/" }, { name: `Επισκευή Κινητών ${area.name}`, path: area.path }]));

  const others = areas.filter((a) => a.slug !== area.slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [area.slug]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-28 pb-24" data-testid={`area-page-${area.slug}`}>
        <section className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <nav className="text-xs text-emerald-200/60 flex items-center gap-2" aria-label="breadcrumb">
              <Link to="/" className="hover:text-emerald-300">Αρχική</Link>
              <span>/</span>
              <span className="text-emerald-200">Επισκευή Κινητών {area.name}</span>
            </nav>
            <div className="mt-4 text-xs uppercase tracking-[0.3em] text-emerald-300/80 inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Κατ' οίκον εξυπηρέτηση {area.inArea}
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight" data-testid="area-h1">
              Επισκευή Κινητών <span className="text-emerald-400">{area.name}</span>
            </h1>
            <p className="mt-4 text-lg text-emerald-50/75">
              Κινητά, laptop, tablet, υπολογιστές & κονσόλες – επισκευή στον χώρο σας {area.inArea} από τη Repairzone Π.Κ, με δωρεάν διάγνωση και 6 μήνες εγγύηση.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`tel:+30${strip(businessInfo.mobile)}`} data-testid="area-call-btn" className="btn-primary rounded-md px-5 py-3 inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> {businessInfo.mobile}
              </a>
              <a href={`tel:+30${strip(businessInfo.mobile2)}`} data-testid="area-call2-btn" className="btn-outline rounded-md px-5 py-3 inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> {businessInfo.mobile2}
              </a>
              <a href="/#contact" className="btn-outline rounded-md px-5 py-3 inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Φόρμα επικοινωνίας
              </a>
            </div>
            <div className="mt-8 grid sm:grid-cols-3 gap-3 text-sm">
              {[
                { icon: Clock, t: "Χρόνος άφιξης", d: area.eta },
                { icon: Home, t: "Κατ' οίκον", d: "Ή δωρεάν παραλαβή & παράδοση" },
                { icon: Shield, t: "Εγγύηση", d: "6 μήνες σε κάθε επισκευή" },
              ].map(({ icon: I, t, d }) => (
                <div key={t} className="pcb-card p-4">
                  <I className="w-5 h-5 text-emerald-400" />
                  <div className="mt-2 text-white font-semibold">{t}</div>
                  <div className="text-emerald-100/70 text-xs mt-0.5">{d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-emerald-500/30 glow-green">
            <img src={heroImage} alt={`Τεχνικός Repairzone επισκευάζει πλακέτα – επισκευή κινητών ${area.inArea}`} width="1200" height="800" className="w-full h-[380px] lg:h-[480px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#04101c] via-transparent to-transparent" />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-5 lg:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Επισκευή συσκευών {area.inArea} – πώς δουλεύουμε</h2>
          <div className="mt-5 space-y-4 text-emerald-50/80 leading-relaxed">
            {area.intro.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Υπηρεσίες & ενδεικτικές τιμές {area.inArea}</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Smartphone;
              return (
                <div key={s.id} className="pcb-card p-6" data-testid={`area-service-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 rounded-md bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <span className="text-emerald-300 font-semibold text-sm">{s.price}</span>
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-white">{s.title} {area.name}</h3>
                  <p className="mt-2 text-sm text-emerald-50/70">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Περιοχές που εξυπηρετούμε {area.inArea} και γύρω</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-sm">{area.name}</span>
            {area.nearby.map((n) => (
              <span key={n} className="px-3 py-1.5 rounded-md bg-[#04101c] border border-emerald-900/70 text-emerald-100/80 text-sm">{n}</span>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-5 lg:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Συχνές ερωτήσεις για {area.name}</h2>
          <div className="mt-6"><AreaFaq faqs={area.faqs} /></div>
        </section>

        <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-20">
          <div className="pcb-card p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="text-white font-bold text-2xl">Χρειάζεστε επισκευή {area.inArea} σήμερα;</div>
              <div className="text-emerald-50/70 mt-2">Καλέστε ή στείλτε WhatsApp/Viber. Δωρεάν διάγνωση, ξεκάθαρη τιμή πριν ξεκινήσουμε.</div>
            </div>
            <a href={`tel:+30${strip(businessInfo.mobile)}`} className="btn-primary rounded-md px-6 py-3.5 inline-flex items-center justify-center gap-2 shrink-0">
              <Phone className="w-4 h-4" /> Καλέστε τώρα
            </a>
          </div>
          <div className="mt-10">
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/80">Άλλες περιοχές</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {others.map((a) => (
                <Link key={a.slug} to={a.path} data-testid={`area-link-${a.slug}`} className="px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-200 text-sm hover:bg-emerald-500/15 transition-colors inline-flex items-center gap-1">
                  Επισκευή κινητών {a.name} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AreaLandingPage;
